/**
 * Admin Dashboard — Premium SaaS UI (data via getAdminDashboard).
 * Metrics are platform-wide aggregates (all users); see metric_windows from API for exact scopes.
 */

import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { getAdminDashboard } from '../services/api';
import { formatRelative as tzFormatRelative } from '../utils/time';

const defaultMetrics = {
  total_users: 0, total_files: 0, total_storage_mb: 0,
  total_audit_events: 0,
  total_login_events: 0, total_upload_events: 0, total_decryption_events: 0,
  recent_logins: 0, recent_uploads: 0,
  recent_decryptions: 0, failed_otp_attempts: 0, active_alerts: 0,
  new_alert_count: 0, active_alert_count: 0, pending_deletions: 0,
  login_trend: [0, 0, 0, 0, 0, 0, 0],
  upload_trend: [0, 0, 0, 0, 0, 0, 0],
  otp_fail_trend: [0, 0, 0, 0, 0, 0, 0],
  security_levels: { standard: 0, enhanced: 0, maximum: 0 },
  recent_activity: [],
  metric_windows: {},
  dashboard_risk_policy: {},
};

function getRelativeTime(isoStr) { return tzFormatRelative(isoStr); }

/** Half-week vs half-week % change from existing 7-day series (display only). */
function trendPctFromHalves(arr) {
  if (!arr || arr.length < 2) return null;
  const mid = Math.floor(arr.length / 2);
  const first = arr.slice(0, mid).reduce((s, v) => s + v, 0);
  const second = arr.slice(mid).reduce((s, v) => s + v, 0);
  if (first === 0 && second === 0) return null;
  if (first === 0) return second > 0 ? 100 : null;
  if (second === 0 && first > 0) return -100;
  return Math.round(((second - first) / first) * 100);
}

function TrendChart({ title, subtitle, values, labels, stroke, fillStart, fillEnd }) {
  const lineRef = useRef(null);
  const [lineLen, setLineLen] = useState(800);
  const width = 560;
  const height = 132;
  const padX = 14;
  const padTop = 12;
  const padBottom = 24;
  const max = Math.max(...values, 1);
  const drawHeight = height - padTop - padBottom;
  const step = (width - padX * 2) / Math.max(values.length - 1, 1);
  const points = values.map((v, i) => {
    const x = padX + i * step;
    const y = padTop + drawHeight - (v / max) * drawHeight;
    return { x, y };
  });
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - padBottom} L ${points[0].x} ${height - padBottom} Z`;
  const gradId = `grad-${title.replace(/\s+/g, '-').toLowerCase()}`;

  useLayoutEffect(() => {
    const el = lineRef.current;
    if (el && typeof el.getTotalLength === 'function') {
      const L = el.getTotalLength();
      if (L > 0) setLineLen(L);
    }
  }, [linePath]);

  return (
    <div style={st.trendBlock}>
      <div style={st.trendHead}>
        <div>
          <span style={st.trendName}>{title}</span>
          {subtitle ? <div style={st.trendSubtitle}>{subtitle}</div> : null}
        </div>
        <span style={st.trendTotal} title="Sum of daily counts in this 7-day window (all users)">
          {values.reduce((a, b) => a + b, 0)}
        </span>
      </div>
      <div className="premium-chart-box">
        <svg viewBox={`0 0 ${width} ${height}`} style={st.chartSvg}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={fillStart} />
              <stop offset="50%" stopColor={fillStart} stopOpacity={0.4} />
              <stop offset="100%" stopColor={fillEnd} stopOpacity={0.06} />
            </linearGradient>
          </defs>
          <path className="premium-chart-area" d={areaPath} fill={`url(#${gradId})`} />
          <path
            ref={lineRef}
            className="premium-chart-line"
            d={linePath}
            fill="none"
            stroke={stroke}
            strokeWidth="2.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ ['--chart-line-len']: lineLen }}
          />
        </svg>
        <div style={st.xLabels}>
          {labels.map((label, i) => <span key={`${title}-${i}`}>{label}</span>)}
        </div>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const inFlightRef = useRef(false);

  useEffect(() => { loadMetrics(); }, []);
  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      loadMetrics();
    }, 60000);
    return () => clearInterval(id);
  }, []);

  const loadMetrics = async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const raw = await getAdminDashboard();
      const merged = { ...defaultMetrics };
      if (raw && typeof raw === 'object') {
        Object.keys(raw).forEach((k) => {
          if (raw[k] !== undefined && raw[k] !== null) merged[k] = raw[k];
        });
      }
      setMetrics(merged);
    } catch {
      setMetrics({ ...defaultMetrics });
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
      inFlightRef.current = false;
    }
  };

  if (loading) {
    return (
      <div className="admin-dash-loading" style={{ display: 'flex', justifyContent: 'center', padding: 72 }}>
        <div style={{ width: 32, height: 32, borderWidth: 3, borderStyle: 'solid', borderColor: 'var(--border)', borderTopColor: 'var(--blue)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      </div>
    );
  }

  const eventColors = {
    UPLOAD: 'var(--blue)',
    FILE_UPLOAD: 'var(--blue)',
    LOGIN: 'var(--green)',
    LOGIN_SUCCESS: 'var(--green)',
    LOGIN_FAIL: 'var(--red)',
    OTP_VERIFIED: 'var(--green)',
    OTP_SUCCESS: 'var(--green)',
    OTP_GENERATED: '#ea580c',
    OTP_FAILED: 'var(--red)',
    OTP_FAIL: 'var(--red)',
    DECRYPT_REQUEST: 'var(--blue)',
    ENCRYPTED_DOWNLOAD: 'var(--blue)',
    FILE_DOWNLOAD: 'var(--blue)',
    ADMIN_ACCESS: '#ea580c',
    SECURITY_ALERT: 'var(--red)',
    FILE_DELETED: 'var(--red)',
    FILE_DELETE: 'var(--red)',
    DELETE_USER: 'var(--red)',
    ACCESS_REQUEST: '#6366f1',
  };

  const days = (() => {
    const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const labels = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      labels.push(names[d.getDay()]);
    }
    return labels;
  })();

  const loginTrend = metrics.login_trend || defaultMetrics.login_trend;
  const uploadTrend = metrics.upload_trend || defaultMetrics.upload_trend;
  const otpFailTrend = metrics.otp_fail_trend || defaultMetrics.otp_fail_trend;
  const secLevels = metrics.security_levels || defaultMetrics.security_levels;
  const rawRecentActivity = metrics.recent_activity || [];
  const recentActivity = rawRecentActivity.filter(
    (a) => a.event_type !== 'FILE_DELETE_SCHEDULED' && a.event_type !== 'FILE_DELETE_CANCELED'
  );
  const totalSecFiles = (secLevels.standard || 0) + (secLevels.enhanced || 0) + (secLevels.maximum || 0);

  const policy = metrics.dashboard_risk_policy && typeof metrics.dashboard_risk_policy === 'object'
    ? metrics.dashboard_risk_policy
    : {};
  const otpHighThreshold = typeof policy.failed_otp_high_if_strictly_greater_than === 'number'
    ? policy.failed_otp_high_if_strictly_greater_than
    : 5;
  const otpWindowHours = typeof policy.failed_otp_window_hours === 'number'
    ? policy.failed_otp_window_hours
    : 24;

  const failedOtp = metrics.failed_otp_attempts || 0;
  const pendingDel = metrics.pending_deletions || 0;
  const newAlerts = metrics.new_alert_count || 0;
  const openAlerts = metrics.active_alert_count != null ? metrics.active_alert_count : (metrics.active_alerts || 0);

  let riskLevel = 'Low';
  if (failedOtp > otpHighThreshold || newAlerts > 0 || pendingDel > 0) {
    riskLevel = 'High';
  } else if (failedOtp > 0 || openAlerts > 0) {
    riskLevel = 'Elevated';
  }

  const trendLogins = trendPctFromHalves(loginTrend);
  const trendUploads = trendPctFromHalves(uploadTrend);

  const trendDeltaShort = '7-day split';
  const trendDeltaTitle = 'Change between first half and second half of the same 7-day trend chart (not calendar week-over-week)';

  const loginCount = metrics.total_login_events ?? metrics.recent_logins ?? 0;
  const uploadCount = metrics.total_upload_events ?? metrics.recent_uploads ?? 0;
  const decryptCount = metrics.total_decryption_events ?? metrics.recent_decryptions ?? 0;

  const statsCards = [
    {
      label: 'Total Users',
      val: metrics.total_users,
      icon: iconUsers,
      trendPct: null,
      hint: 'Accounts not soft-deleted (all roles)',
    },
    {
      label: 'Files',
      val: metrics.total_files,
      icon: iconLock,
      trendPct: null,
      hint: 'Encrypted file records in the system (all users)',
    },
    {
      label: 'Audit Events',
      val: metrics.total_audit_events,
      icon: iconDoc,
      trendPct: null,
      hint: 'All-time audit log rows (all users)',
    },
    {
      label: 'Login events',
      val: loginCount,
      icon: iconLogin,
      trendPct: trendLogins,
      hint: 'All-time LOGIN audit events · all users (not “last 7 days”)',
    },
    {
      label: 'Upload events',
      val: uploadCount,
      icon: iconUpload,
      trendPct: trendUploads,
      hint: 'All-time UPLOAD audit events · all users (not “last 7 days”)',
    },
  ];

  return (
    <div className="admin-page-shell admin-dashboard-shell admin-dashboard-premium" style={st.shell}>
      <div style={st.toolbar} className="admin-dash-fade-up">
        <div>
          <p style={st.toolbarHint}>
            Platform-wide metrics · Last updated: {lastRefresh.toLocaleTimeString('en-IE')} (local time)
          </p>
        </div>
        <button type="button" onClick={loadMetrics} style={st.refreshBtn} className="admin-dash-btn-premium" title="Refresh metrics">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>
          Refresh
        </button>
      </div>

      <div style={st.statsRow} className="admin-dash-fade-up admin-dash-delay-1">
        {statsCards.map((m, i) => (
          <div key={i} className="premium-stat-card" title={m.hint || undefined}>
            <div className="premium-stat-icon">{m.icon}</div>
            <div style={st.statValue}>{m.val.toLocaleString()}</div>
            <div style={st.statLabel}>{m.label}</div>
            {m.trendPct != null ? (
              <span className={`premium-stat-trend ${m.trendPct >= 0 ? 'is-up' : 'is-down'}`} title={trendDeltaTitle}>
                {m.trendPct >= 0 ? '+' : ''}{m.trendPct}% <span style={{ fontWeight: 600, opacity: 0.85 }}>({trendDeltaShort})</span>
              </span>
            ) : (
              <span className="premium-stat-trend premium-stat-trend--na">—</span>
            )}
          </div>
        ))}
      </div>

      <div style={st.row1} className="admin-dash-fade-up admin-dash-delay-2 admin-dash-row1">
        <div className="premium-panel">
          <h3 style={st.cardTitle}>
            Activity trends <span style={st.cardTitleMuted}>Last 7 days (UTC days) · all users</span>
          </h3>
          <p style={st.panelMicrocopy}>
            Each line is the daily count of audit events across the whole platform (not one user). Numbers on the right are the week total for that chart.
          </p>
          <TrendChart
            title="Logins"
            subtitle="Per-day LOGIN events"
            values={loginTrend}
            labels={days}
            stroke="#3b82f6"
            fillStart="rgba(59,130,246,0.35)"
            fillEnd="rgba(59,130,246,0.03)"
          />
          <TrendChart
            title="Uploads"
            subtitle="Per-day UPLOAD events"
            values={uploadTrend}
            labels={days}
            stroke="#16a34a"
            fillStart="rgba(22,163,74,0.32)"
            fillEnd="rgba(22,163,74,0.03)"
          />
          <TrendChart
            title="OTP failures"
            subtitle="Per-day OTP_FAILED events"
            values={otpFailTrend}
            labels={days}
            stroke="#dc2626"
            fillStart="rgba(220,38,38,0.28)"
            fillEnd="rgba(220,38,38,0.03)"
          />
        </div>

        <div
          className={
            `premium-panel premium-panel--compact premium-panel--security-` +
            (riskLevel === 'High' ? 'high' : riskLevel === 'Elevated' ? 'medium' : 'low')
          }
        >
          <h3 style={st.cardTitle}>Security summary</h3>
          <div style={st.riskPill}>
            <span style={st.riskLabel}>Risk level</span>
            <span
              className={
                'risk-level-badge ' +
                (riskLevel === 'High' ? 'risk-level-badge--high' : riskLevel === 'Elevated' ? 'risk-level-badge--medium' : 'risk-level-badge--low')
              }
            >
              {riskLevel}
            </span>
          </div>
          <div style={st.summaryList}>
            <div style={st.summaryRow}>
              <span style={st.summaryKey} title={`OTP_FAILED audit rows in the last ${otpWindowHours}h UTC · all users`}>
                Failed OTP ({otpWindowHours}h)
              </span>
              <strong style={{ ...st.summaryVal, color: failedOtp > otpHighThreshold ? 'var(--red)' : 'var(--text-primary)' }}>{failedOtp}</strong>
            </div>
            <div style={st.summaryRow}>
              <span style={st.summaryKey} title="Files with a scheduled deletion time still in the future">
                Pending deletions
              </span>
              <strong style={{ ...st.summaryVal, color: pendingDel > 0 ? '#ea580c' : 'var(--text-primary)' }}>{pendingDel}</strong>
            </div>
            <div style={st.summaryRow}>
              <span style={st.summaryKey} title="Security alerts in NEW status (badge count)">
                New alerts
              </span>
              <strong style={{ ...st.summaryVal, color: newAlerts > 0 ? 'var(--red)' : 'var(--text-primary)' }}>{newAlerts}</strong>
            </div>
            <div style={st.summaryRow}>
              <span style={st.summaryKey} title="NEW + SEEN + REVIEWED (not yet RESOLVED)">
                Open alerts
              </span>
              <strong style={{ ...st.summaryVal, color: openAlerts > 0 ? '#ea580c' : 'var(--text-primary)' }}>{openAlerts}</strong>
            </div>
          </div>
        </div>
      </div>

      <div style={st.row2} className="admin-dash-fade-up admin-dash-delay-3 admin-dash-row2">
        <div className="premium-panel">
          <h3 style={st.cardTitle}>Storage &amp; encryption</h3>
          <div style={st.storageGrid}>
            <div style={st.storageCell} title="Total encrypted file records (all users)">
              <div style={st.storageBig}>{metrics.total_files}</div>
              <div style={st.storageSmall}>Files</div>
            </div>
            <div style={st.storageCell} title="Sum of stored ciphertext sizes (metadata only)">
              <div style={st.storageBig}>{metrics.total_storage_mb}</div>
              <div style={st.storageSmall}>MB stored</div>
            </div>
            <div style={st.storageCell} title="All-time ENCRYPTED_DOWNLOAD audit events · all users">
              <div style={st.storageBig}>{decryptCount}</div>
              <div style={st.storageSmall}>Decrypt events</div>
            </div>
          </div>
          <p style={st.encSubtitle}>Files by encryption level</p>
          <div style={st.progressTrack}>
            {totalSecFiles > 0 ? (
              <>
                <div style={{ ...st.progressSeg, width: `${(secLevels.standard / totalSecFiles) * 100}%`, background: '#94a3b8' }} title={`Standard: ${secLevels.standard}`} />
                <div style={{ ...st.progressSeg, width: `${(secLevels.enhanced / totalSecFiles) * 100}%`, background: 'var(--blue)' }} title={`Enhanced: ${secLevels.enhanced}`} />
                <div style={{ ...st.progressSeg, width: `${(secLevels.maximum / totalSecFiles) * 100}%`, background: 'var(--red)' }} title={`Maximum: ${secLevels.maximum}`} />
              </>
            ) : (
              <div style={{ ...st.progressSeg, width: '100%', background: 'var(--border)' }} />
            )}
          </div>
          <div style={st.legendRow}>
            <LegendDot color="#94a3b8" label={`Standard (${secLevels.standard || 0})`} />
            <LegendDot color="var(--blue)" label={`Enhanced (${secLevels.enhanced || 0})`} />
            <LegendDot color="var(--red)" label={`Maximum (${secLevels.maximum || 0})`} />
          </div>
        </div>

        <div className="premium-panel">
          <h3 style={st.cardTitle}>Recent activity</h3>
          <div style={st.timeline}>
            {recentActivity.length === 0 ? (
              <div style={st.emptyAct}>No recent activity</div>
            ) : (
              recentActivity.slice(0, 8).map((a, i) => (
                <div key={i} style={st.timelineRow}>
                  <div style={{ ...st.timelineDot, background: eventColors[a.event_type] || 'var(--text-muted)' }} />
                  <div style={st.timelineBody}>
                    <div style={st.timelineDesc}>{a.description}</div>
                    <div style={st.timelineMeta}>{a.user}</div>
                  </div>
                  <div style={st.timelineTime}>{getRelativeTime(a.time)}</div>
                </div>
              ))
            )}
          </div>
          <p style={st.footerHint}>Full history is available under <strong>Audit Logs</strong> in the sidebar.</p>
        </div>
      </div>

      <div style={st.policy} className="admin-dash-fade-up admin-dash-delay-3 premium-panel-inset">
        <div style={st.policyHead}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
          <strong>Admin policy</strong>
        </div>
        <span style={st.policyText}>
          You can view aggregated metrics and activity for all users. File contents stay encrypted on the server; audit logs are append-only for accountability.
        </span>
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>
      <div style={{ width: 8, height: 8, borderRadius: 3, background: color, flexShrink: 0 }} />
      {label}
    </div>
  );
}

const iconUsers = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
const iconLock = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>;
const iconDoc = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>;
const iconLogin = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></svg>;
const iconUpload = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" /><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" /></svg>;

const st = {
  shell: {
    maxWidth: 1600,
    margin: '0 auto',
    padding: '0 0 8px',
    background: 'transparent',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 32,
  },
  toolbarHint: { margin: 0, fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' },
  refreshBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    borderRadius: 12,
    border: '1px solid var(--border)',
    background: 'var(--bg-card)',
    color: 'var(--text-primary)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxShadow: 'var(--shadow-sm)',
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 32,
    marginBottom: 32,
  },
  statValue: { fontSize: 36, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.035em', lineHeight: 1.08 },
  statLabel: { fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginTop: 8 },
  row1: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.45fr) minmax(260px, 0.55fr)',
    gap: 32,
    marginBottom: 32,
    alignItems: 'start',
  },
  row2: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: 32,
    marginBottom: 32,
    alignItems: 'start',
  },
  cardTitle: { margin: '0 0 22px', fontSize: 19, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '0.02em' },
  cardTitleMuted: { fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginLeft: 10, letterSpacing: '0.01em' },
  panelMicrocopy: { margin: '-12px 0 18px', fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', lineHeight: 1.45 },
  trendBlock: { marginBottom: 22 },
  trendHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 },
  trendName: { fontSize: 13, fontWeight: 700, color: 'var(--text-body)', letterSpacing: '0.01em', transition: 'color 0.3s cubic-bezier(0.4, 0, 0.2, 1)' },
  trendSubtitle: { fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', marginTop: 4, letterSpacing: '0.02em' },
  trendTotal: { fontSize: 12, color: 'var(--text-muted)', fontWeight: 700 },
  chartSvg: { width: '100%', height: 128, display: 'block' },
  xLabels: { display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textAlign: 'center', marginTop: 4 },
  riskPill: {
    padding: '14px 16px',
    borderRadius: 14,
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border)',
    marginBottom: 16,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: 'var(--shadow-sm)',
  },
  riskLabel: { fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' },
  summaryList: { display: 'flex', flexDirection: 'column', gap: 0 },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid var(--border)',
  },
  summaryKey: { fontSize: 13, fontWeight: 600, color: 'var(--text-body)' },
  summaryVal: { fontSize: 15, fontWeight: 800 },
  storageGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 22 },
  storageCell: { textAlign: 'center', padding: '16px 8px', background: 'var(--bg-elevated)', borderRadius: 14, border: '1px solid var(--border)' },
  storageBig: { fontSize: 26, fontWeight: 800, color: 'var(--text-primary)' },
  storageSmall: { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginTop: 4 },
  encSubtitle: { fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.06em' },
  progressTrack: { display: 'flex', height: 12, borderRadius: 8, overflow: 'hidden', background: 'var(--bg-elevated)', border: '1px solid var(--border)' },
  progressSeg: { height: '100%', transition: 'width 0.5s ease' },
  legendRow: { display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' },
  timeline: { display: 'flex', flexDirection: 'column' },
  emptyAct: { textAlign: 'center', padding: '28px 12px', color: 'var(--text-muted)', fontSize: 13, fontWeight: 600 },
  timelineRow: {
    display: 'grid',
    gridTemplateColumns: '10px 1fr auto',
    gap: 12,
    alignItems: 'start',
    padding: '14px 0',
    borderBottom: '1px solid var(--border)',
  },
  timelineDot: { width: 8, height: 8, borderRadius: '50%', marginTop: 5, flexShrink: 0 },
  timelineBody: { minWidth: 0 },
  timelineDesc: { fontSize: 14, fontWeight: 600, color: 'var(--text-body)', lineHeight: 1.4 },
  timelineMeta: { fontSize: 12, color: 'var(--text-muted)', marginTop: 2, fontWeight: 500 },
  timelineTime: { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', whiteSpace: 'nowrap', textAlign: 'right' },
  footerHint: { margin: '18px 0 0', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 },
  policy: {
    padding: '18px 20px',
    borderRadius: 16,
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border)',
  },
  policyHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' },
  policyText: { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 },
};

export default AdminDashboard;
